import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Users, Trash2, Settings, Loader2, Mail, X,
  Crown, Shield, User, UserPlus,
} from 'lucide-react';
import { api } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

// ── Types ──

interface ApiMember {
  id: string;
  user: { id: string; full_name: string; email: string; avatar_url: string | null };
  role: string;
  can_view_groups: boolean;
  can_view_teams: boolean;
  can_view_sprints: boolean;
  can_view_goals: boolean;
  can_view_tasks: boolean;
  can_view_performance: boolean;
  can_view_reports: boolean;
  joined_at: string;
}

interface Member {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  role: string;
  can_view_groups: boolean;
  can_view_teams: boolean;
  can_view_sprints: boolean;
  can_view_goals: boolean;
  can_view_tasks: boolean;
  can_view_performance: boolean;
  can_view_reports: boolean;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  invited_by: { id: string; full_name: string; email: string } | null;
}

interface SearchUser {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: { id: string; name: string } | null;
}

// ── Helpers ──

const PERMISSION_KEYS = [
  { key: 'can_view_groups', label: 'Groups' },
  { key: 'can_view_teams', label: 'Teams' },
  { key: 'can_view_sprints', label: 'Sprints' },
  { key: 'can_view_goals', label: 'Goals' },
  { key: 'can_view_tasks', label: 'Tasks' },
  { key: 'can_view_performance', label: 'Performance' },
  { key: 'can_view_reports', label: 'Reports' },
] as const;

function flattenMember(m: ApiMember): Member {
  return {
    id: m.id,
    user_id: m.user.id,
    full_name: m.user.full_name,
    email: m.user.email,
    avatar_url: m.user.avatar_url,
    role: m.role,
    can_view_groups: m.can_view_groups,
    can_view_teams: m.can_view_teams,
    can_view_sprints: m.can_view_sprints,
    can_view_goals: m.can_view_goals,
    can_view_tasks: m.can_view_tasks,
    can_view_performance: m.can_view_performance,
    can_view_reports: m.can_view_reports,
  };
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const RoleIcon = ({ role }: { role: string }) => {
  if (role === 'owner') return <Crown className="h-4 w-4 text-yellow-500" />;
  if (role === 'admin') return <Shield className="h-4 w-4 text-blue-500" />;
  return <User className="h-4 w-4 text-muted-foreground" />;
};

// ── Component ──

export function WorkspaceMembersDialog({ open, onOpenChange, workspace }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permMember, setPermMember] = useState<Member | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);

  const membersKey = ['workspace-members', workspace?.id];
  const invitationsKey = ['workspace-invitations', workspace?.id];

  // ── Queries ──

  const { data: members, isLoading: membersLoading } = useQuery<Member[]>({
    queryKey: membersKey,
    queryFn: () =>
      api.get(`/workspaces/${workspace!.id}/members`, { params: { limit: 100 } })
        .then((r) => (r.data.data as ApiMember[]).map(flattenMember)),
    enabled: open && !!workspace?.id,
  });

  const { data: invitations, isLoading: invitationsLoading } = useQuery<Invitation[]>({
    queryKey: invitationsKey,
    queryFn: () =>
      api.get(`/workspaces/${workspace!.id}/invitations`).then((r) => r.data.data),
    enabled: open && !!workspace?.id,
  });

  const { data: searchResults } = useQuery<SearchUser[]>({
    queryKey: ['workspace-user-search', workspace?.id, userSearchQuery],
    queryFn: () =>
      api.get(`/workspaces/${workspace!.id}/users/search`, { params: { q: userSearchQuery } })
        .then((r) => r.data.data),
    enabled: open && !!workspace?.id && userSearchQuery.length >= 2,
  });

  // ── Derived state ──

  const currentMember = members?.find((m) => m.user_id === user?.id);
  const isOwner = currentMember?.role === 'owner';
  const isAdmin = isOwner || currentMember?.role === 'admin';

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) => m.full_name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [members, searchQuery]);

  const pendingInvitations = invitations?.filter((i) => i.status === 'pending') ?? [];

  // ── Mutations ──

  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      api.post(`/workspaces/${workspace!.id}/members/invite`, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      queryClient.invalidateQueries({ queryKey: invitationsKey });
      const msg = res.data.message || 'Invitation sent';
      toast({ title: msg, description: `Invited ${inviteEmail} as ${inviteRole}.` });
      setInviteEmail('');
      setInviteRole('user');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to invite member.',
        variant: 'destructive',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      api.patch(`/workspaces/${workspace!.id}/members/${memberId}`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      toast({ title: 'Role updated' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to update role.',
        variant: 'destructive',
      });
    },
  });

  const updatePermMutation = useMutation({
    mutationFn: ({ memberId, permission }: { memberId: string; permission: Record<string, boolean> }) =>
      api.patch(`/workspaces/${workspace!.id}/members/${memberId}`, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      toast({ title: 'Permission updated' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to update permission.',
        variant: 'destructive',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) =>
      api.delete(`/workspaces/${workspace!.id}/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      toast({ title: 'Member removed' });
      setRemoveDialogOpen(false);
      setMemberToRemove(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to remove member.',
        variant: 'destructive',
      });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (invitationId: string) =>
      api.post(`/workspaces/${workspace!.id}/invitations/${invitationId}/resend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationsKey });
      toast({ title: 'Invitation resent' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to resend invitation.',
        variant: 'destructive',
      });
    },
  });

  const cancelInvMutation = useMutation({
    mutationFn: (invitationId: string) =>
      api.delete(`/workspaces/${workspace!.id}/invitations/${invitationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationsKey });
      toast({ title: 'Invitation cancelled' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to cancel invitation.',
        variant: 'destructive',
      });
    },
  });

  const addExistingUserMutation = useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      api.post(`/workspaces/${workspace!.id}/members/invite`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      toast({ title: 'Member added' });
      setUserSearchQuery('');
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to add user.',
        variant: 'destructive',
      });
    },
  });

  // ── Handlers ──

  const handlePermToggle = (key: string, checked: boolean) => {
    if (!permMember) return;
    updatePermMutation.mutate({ memberId: permMember.id, permission: { [key]: checked } });
    setPermMember((prev) => (prev ? { ...prev, [key]: checked } : null));
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate({ email: inviteEmail.trim().toLowerCase(), role: inviteRole });
  };

  if (!workspace) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Members - {workspace.name}</DialogTitle>
            <DialogDescription>
              Invite new members or manage existing member permissions.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="members" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="members">
                Members ({members?.length ?? 0})
              </TabsTrigger>
              <TabsTrigger value="invitations">
                Invitations ({pendingInvitations.length})
              </TabsTrigger>
              {isAdmin && <TabsTrigger value="invite">Invite</TabsTrigger>}
            </TabsList>

            {/* ── Tab 1: Members ── */}
            <TabsContent value="members" className="flex-1 flex flex-col min-h-0 mt-4">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <ScrollArea className="h-[350px]">
                {membersLoading ? (
                  <div className="space-y-3 p-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                        <Skeleton className="h-9 w-9 rounded-full" />
                        <div className="space-y-1 flex-1">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-40" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Users className="h-8 w-8 mb-2" />
                    <p className="text-sm">{searchQuery ? 'No members match your search.' : 'No members found.'}</p>
                  </div>
                ) : (
                  <div className="space-y-2 pr-4">
                    {filteredMembers.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-brand-primary text-white text-xs">
                              {getInitials(member.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{member.full_name}</span>
                              <RoleIcon role={member.role} />
                            </div>
                            <span className="text-xs text-muted-foreground">{member.email}</span>
                          </div>
                        </div>

                        {member.role === 'owner' ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            Owner
                          </Badge>
                        ) : isAdmin ? (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Select
                              value={member.role}
                              onValueChange={(role) =>
                                updateRoleMutation.mutate({ memberId: member.id, role })
                              }
                              disabled={!isOwner && member.role === 'admin'}
                            >
                              <SelectTrigger className="h-7 w-[90px] text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {isOwner && <SelectItem value="admin">Admin</SelectItem>}
                                <SelectItem value="user">User</SelectItem>
                              </SelectContent>
                            </Select>

                            {member.role === 'user' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => { setPermMember(member); setPermDialogOpen(true); }}
                              >
                                <Settings className="h-3.5 w-3.5" />
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => { setMemberToRemove(member); setRemoveDialogOpen(true); }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="outline" className={member.role === 'admin' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-700 border-gray-200'}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* ── Tab 2: Invitations ── */}
            <TabsContent value="invitations" className="flex-1 min-h-0 mt-4">
              <ScrollArea className="h-[400px]">
                {invitationsLoading ? (
                  <div className="space-y-3 p-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
                        <Skeleton className="h-5 w-5" />
                        <div className="space-y-1 flex-1">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : pendingInvitations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <Mail className="h-8 w-8 mb-2" />
                    <p className="text-sm">No pending invitations</p>
                  </div>
                ) : (
                  <div className="space-y-2 pr-4">
                    {pendingInvitations.map((invitation) => (
                      <div key={invitation.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div>
                            <span className="font-medium text-sm">{invitation.email}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Badge variant="outline" className="text-xs">
                                {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Expires {new Date(invitation.expires_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {isAdmin && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendMutation.mutate(invitation.id)}
                              disabled={resendMutation.isPending}
                            >
                              Resend
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => cancelInvMutation.mutate(invitation.id)}
                              disabled={cancelInvMutation.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* ── Tab 3: Invite ── */}
            {isAdmin && (
              <TabsContent value="invite" className="mt-4 space-y-4">
                {/* Section 1: Invite by Email */}
                <div>
                  <Label className="text-sm font-medium">Invite by Email</Label>
                  <form onSubmit={handleInvite} className="flex gap-2 mt-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger className="w-[110px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isOwner && <SelectItem value="admin">Admin</SelectItem>}
                        <SelectItem value="user">User</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="submit"
                      disabled={!inviteEmail.trim() || inviteMutation.isPending}
                      className="bg-brand-primary hover:bg-brand-primary-dark text-white"
                    >
                      {inviteMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-1.5" />
                          Invite
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                <Separator />

                {/* Section 2: Add Existing User */}
                <div>
                  <Label className="text-sm font-medium">Add Existing User</Label>
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="mt-2"
                  />
                  {searchResults && searchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                      {searchResults.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-2 hover:bg-muted">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-brand-primary text-white text-xs">
                                {getInitials(u.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-sm">{u.full_name}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addExistingUserMutation.mutate({ email: u.email, role: 'user' })}
                            disabled={addExistingUserMutation.isPending}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {userSearchQuery.length >= 2 && searchResults && searchResults.length === 0 && (
                    <p className="mt-2 text-sm text-muted-foreground text-center py-3">No users found matching your search.</p>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ── Permissions Sub-Dialog ── */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Permissions - {permMember?.full_name}</DialogTitle>
            <DialogDescription>
              Configure which pages this member can access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {PERMISSION_KEYS.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <Label htmlFor={`perm-${key}`} className="text-sm font-normal cursor-pointer">
                  {label}
                </Label>
                <Switch
                  id={`perm-${key}`}
                  checked={permMember ? !!(permMember as unknown as Record<string, boolean>)[key] : false}
                  onCheckedChange={(checked) => handlePermToggle(key, checked)}
                  disabled={updatePermMutation.isPending}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Remove Member Confirmation ── */}
      <ConfirmDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        title="Remove Member"
        description={`Are you sure you want to remove ${memberToRemove?.full_name} from this workspace? They will lose access to all workspace resources.`}
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={() => { if (memberToRemove) removeMutation.mutate(memberToRemove.id); }}
        isLoading={removeMutation.isPending}
      />
    </>
  );
}
